import { useEffect, useState } from "react";
import { X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { adminCreateBooking, adminUpdateBooking, adminDeleteBooking } from "@/lib/api";
import { STATUSES, EVENT_TYPES, PACKAGES, ADD_ONS } from "./constants";

const empty = {
  event_date: "", event_type: "Beachfront Wedding", guest_count: 40, location: "",
  package_id: "premium", add_ons: [], status: "enquiry",
  customer_name: "", customer_email: "", customer_phone: "", notes: "", total_estimate: 0,
};

export default function BookingModal({ open, onClose, onSaved, token, initial }) {
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const editing = Boolean(initial?.id);

  useEffect(() => { if (open) setForm(initial ? { ...empty, ...initial } : empty); }, [open, initial]);

  if (!open) return null;

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleAddOn = (id) => upd("add_ons", form.add_ons.includes(id) ? form.add_ons.filter((x) => x !== id) : [...form.add_ons, id]);

  const save = async () => {
    if (!form.event_date || !form.event_type) { toast.error("Date and event type are required."); return; }
    setSaving(true);
    try {
      const payload = { ...form, guest_count: parseInt(form.guest_count) || 0, total_estimate: parseInt(form.total_estimate) || 0 };
      const data = editing ? await adminUpdateBooking(initial.id, payload, token) : await adminCreateBooking(payload, token);
      toast.success(editing ? "Booking updated." : "Booking created.");
      onSaved(data);
      onClose();
    } catch { toast.error("Save failed."); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm("Delete this booking?")) return;
    try { await adminDeleteBooking(initial.id, token); toast.success("Deleted."); onSaved(null); onClose(); }
    catch { toast.error("Delete failed."); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-ocean-deep/60 backdrop-blur-sm overflow-y-auto p-4 flex items-start md:items-center justify-center">
      <div data-testid="booking-modal" className="bg-cream rounded-3xl w-full max-w-2xl p-6 md:p-8 my-6 shadow-2xl">
        <div className="flex items-start justify-between mb-6">
          <div>
            <p className="overline text-xs text-coral mb-2">{editing ? "Edit Booking" : "New Booking"}</p>
            <h2 className="font-serif text-3xl text-ocean-deep leading-tight">Event details</h2>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-sand/50"><X className="w-5 h-5 text-ocean" /></button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="overline text-xs text-ocean block mb-2">Event date</label>
            <Input data-testid="bm-date" type="date" value={form.event_date} onChange={(e) => upd("event_date", e.target.value)} className="h-11 bg-white border-sand rounded-xl text-ocean" />
          </div>
          <div>
            <label className="overline text-xs text-ocean block mb-2">Event type</label>
            <Select value={form.event_type} onValueChange={(v) => upd("event_type", v)}>
              <SelectTrigger data-testid="bm-type" className="h-11 bg-white border-sand rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent>{EVENT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="overline text-xs text-ocean block mb-2">Guests</label>
            <Input data-testid="bm-guests" type="number" min="1" value={form.guest_count} onChange={(e) => upd("guest_count", e.target.value)} className="h-11 bg-white border-sand rounded-xl" />
          </div>
          <div>
            <label className="overline text-xs text-ocean block mb-2">Package</label>
            <Select value={form.package_id} onValueChange={(v) => upd("package_id", v)}>
              <SelectTrigger data-testid="bm-package" className="h-11 bg-white border-sand rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent>{PACKAGES.map((p) => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <label className="overline text-xs text-ocean block mb-2">Location</label>
            <Input data-testid="bm-location" placeholder="e.g. Noosa Beach, bride's backyard, boat moored at Mooloolaba" value={form.location} onChange={(e) => upd("location", e.target.value)} className="h-11 bg-white border-sand rounded-xl" />
          </div>
          <div className="md:col-span-2">
            <label className="overline text-xs text-ocean block mb-2">Add-ons</label>
            <div className="flex flex-wrap gap-2">
              {ADD_ONS.map((a) => {
                const on = form.add_ons.includes(a.id);
                return (
                  <button key={a.id} type="button" data-testid={`bm-addon-${a.id}`} onClick={() => toggleAddOn(a.id)}
                    className={`text-xs rounded-full px-4 py-2 border transition-colors ${on ? "bg-coral text-white border-coral" : "bg-white border-sand text-ocean hover:border-coral"}`}>
                    {a.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <label className="overline text-xs text-ocean block mb-2">Status</label>
            <Select value={form.status} onValueChange={(v) => upd("status", v)}>
              <SelectTrigger data-testid="bm-status" className="h-11 bg-white border-sand rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent>{STATUSES.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="overline text-xs text-ocean block mb-2">Total estimate (AUD)</label>
            <Input data-testid="bm-total" type="number" min="0" value={form.total_estimate} onChange={(e) => upd("total_estimate", e.target.value)} className="h-11 bg-white border-sand rounded-xl" />
          </div>
          <div>
            <label className="overline text-xs text-ocean block mb-2">Customer name</label>
            <Input data-testid="bm-name" value={form.customer_name} onChange={(e) => upd("customer_name", e.target.value)} className="h-11 bg-white border-sand rounded-xl" />
          </div>
          <div>
            <label className="overline text-xs text-ocean block mb-2">Customer email</label>
            <Input data-testid="bm-email" type="email" value={form.customer_email} onChange={(e) => upd("customer_email", e.target.value)} className="h-11 bg-white border-sand rounded-xl" />
          </div>
          <div className="md:col-span-2">
            <label className="overline text-xs text-ocean block mb-2">Customer phone</label>
            <Input data-testid="bm-phone" value={form.customer_phone} onChange={(e) => upd("customer_phone", e.target.value)} className="h-11 bg-white border-sand rounded-xl" />
          </div>
          <div className="md:col-span-2">
            <label className="overline text-xs text-ocean block mb-2">Notes</label>
            <Textarea data-testid="bm-notes" value={form.notes} onChange={(e) => upd("notes", e.target.value)} className="min-h-24 bg-white border-sand rounded-xl" />
          </div>
        </div>

        <div className="flex justify-between items-center mt-7">
          <div>{editing && <button type="button" onClick={remove} className="text-xs text-red-500 overline">Delete</button>}</div>
          <div className="flex gap-3">
            <button onClick={onClose} className="rounded-full px-5 py-2.5 border border-sand text-ocean text-xs overline hover:bg-sand/40">Cancel</button>
            <button data-testid="bm-save" onClick={save} disabled={saving} className="rounded-full bg-coral text-white px-6 py-2.5 text-xs overline hover:bg-[#c9633f] disabled:opacity-60 flex items-center gap-2">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}{editing ? "Save" : "Create"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
